import { useCallback, useEffect, useState } from "react";
import {
  FeedbackDialogViewModel,
  UseFeedbackDialogViewModelArgs,
} from "./FeedbackDialog.ViewModel.types";

const DEFAULT_FEEDBACK_TYPE = "General Feedback";

export function useFeedbackDialogViewModel(
  args: UseFeedbackDialogViewModelArgs,
): FeedbackDialogViewModel {
  const { feedbackApi, boardId, isOpen, onClose, type } = args;

  const [rating, setRating] = useState(0);
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setRating(0);
    setDescription("");
    setHasError(false);
  }, [isOpen]);

  const canSubmit = rating > 0 && description.trim().length > 0 && !isSaving;

  const submit = useCallback(async () => {
    if (!canSubmit) return;

    setIsSaving(true);
    setHasError(false);
    try {
      await feedbackApi.createFeedback({
        boardId,
        type: type ?? DEFAULT_FEEDBACK_TYPE,
        rating,
        description,
      });
      onClose();
    } catch {
      setHasError(true);
    } finally {
      setIsSaving(false);
    }
  }, [canSubmit, feedbackApi, boardId, type, rating, description, onClose]);

  const cancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return {
    rating,
    description,
    isSaving,
    hasError,
    canSubmit,
    setRating,
    setDescription,
    submit,
    cancel,
  };
}
